/**
 * How a person's access is DISPLAYED — one component, used by the roster row and
 * the expanded detail, so the summary and the detail can never disagree.
 *
 * The distinction this component exists to hold: `roles: null` from the server
 * means "we could not read the grant table", which is NOT the same as "no
 * grants". The old roster rendered both as the string "no roles", which is how a
 * database whose migration never applied looked exactly like a healthy one. Here,
 * unknown is amber and says unknown.
 */
import {
  MODULE_LABEL,
  PAGE_MODULES,
  pagesForModule,
  pagesForRoles,
  type PageModule,
} from '../lib/page-registry';
import { storedRolesFromAdminUser } from '../lib/rbac-model';
import type { AdminUser } from '../types';

/** True when the server explicitly told us it could not resolve roles. */
export function rolesUnavailable(user: AdminUser): boolean {
  return user.roles === null;
}

/** The pages a roster user can reach, grouped by module in registry order. */
export function pagesByModuleFor(
  user: AdminUser,
): { module: PageModule; labels: string[] }[] {
  const pages = pagesForRoles(storedRolesFromAdminUser(user));
  return PAGE_MODULES.map((module) => ({
    module,
    labels: pages.filter((p) => p.module === module).map((p) => p.label),
  })).filter((g) => g.labels.length > 0);
}

/**
 * Compact roster cell: one chip per module the person can reach, showing how many
 * of that module's pages they get. Admin renders as a single emphasised chip
 * because "Admin 1/1" would undersell full platform access.
 */
export function AccessChips({ user }: { user: AdminUser }) {
  if (rolesUnavailable(user)) {
    return (
      <span
        className="rounded-full bg-amber-500/10 px-2 py-0.5 font-inter font-medium text-amber-700 text-xs"
        data-testid="access-unknown"
        title="The server could not read the role table — this is not the same as having no access."
      >
        access unknown
      </span>
    );
  }

  const groups = pagesByModuleFor(user);
  if (groups.length === 0) {
    return (
      <span
        className="rounded-full bg-content-tertiary/15 px-2 py-0.5 font-inter font-medium text-content-secondary text-xs"
        data-testid="access-none"
      >
        no access
      </span>
    );
  }

  return (
    <span className="flex flex-wrap gap-1" data-testid="access-chips">
      {groups.map(({ module, labels }) => {
        const total = pagesForModule(module).length;
        const isAdmin = module === 'admin';
        return (
          <span
            className={`rounded-full px-2 py-0.5 font-inter text-xs ${
              isAdmin
                ? 'bg-surface-action-subtle font-medium text-content-action'
                : 'bg-content-tertiary/15 text-content-secondary'
            }`}
            data-module={module}
            key={module}
            title={labels.join(', ')}
          >
            {isAdmin ? 'Full admin' : MODULE_LABEL[module]}
            {!isAdmin && labels.length < total ? (
              <span className="ml-1 text-content-tertiary">
                {labels.length}/{total}
              </span>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}

/** Expanded detail: every page the person can open, grouped by module. */
export function AccessPageList({ user }: { user: AdminUser }) {
  if (rolesUnavailable(user)) {
    return (
      <p
        className="font-inter text-amber-700 text-xs"
        data-testid="access-page-list-unknown"
      >
        The server could not read the role table, so the pages this person can
        open are unknown. This is a backend problem, not an empty permission set
        — do not re-grant access from this screen until it resolves.
      </p>
    );
  }

  const groups = pagesByModuleFor(user);
  if (groups.length === 0) {
    return (
      <p
        className="font-inter text-content-tertiary text-xs"
        data-testid="access-page-list-empty"
      >
        No pages. This person can sign in but every surface is closed to them
        (default-deny).
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1.5" data-testid="access-page-list">
      {groups.map(({ module, labels }) => (
        <li className="font-inter text-xs" key={module}>
          <span className="font-medium text-content-primary">
            {MODULE_LABEL[module]}
          </span>
          <span className="text-content-secondary"> — {labels.join(', ')}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Where this person came from. Only rendered when it is NOTEWORTHY: a signed-in
 * user missing from the directory enumeration is the case that used to make
 * someone invisible on this page, so it gets said out loud.
 */
export function SourceBadge({ user }: { user: AdminUser }) {
  const identityCount = user.identities?.length ?? 1;
  return (
    <span className="flex flex-wrap gap-1">
      {user.in_directory === false ? (
        <span
          className="rounded-full bg-sky-500/10 px-2 py-0.5 font-inter font-medium text-sky-700 text-xs"
          data-testid="source-not-in-directory"
          title="This person has signed in to the app, but is not in the directory tenant this page enumerates. Sign-in accepts two tenants; the listing reads one."
        >
          signed in · not in directory
        </span>
      ) : null}
      {/*
        One human, two Entra identities (workforce + CIAM). Worth showing because
        roles are stored PER identity: the grant goes to the one they actually sign
        in with, and an admin should be able to see that a choice was made rather
        than discover it when access doesn't work.
      */}
      {identityCount > 1 ? (
        <span
          className="rounded-full bg-violet-500/10 px-2 py-0.5 font-inter font-medium text-violet-700 text-xs"
          data-testid="source-multi-identity"
          title={`This email owns ${identityCount} sign-in identities (two tenants accept this address). Access is granted to the one they actually sign in with: ${user.id}.`}
        >
          {identityCount} sign-in identities
        </span>
      ) : null}
    </span>
  );
}
